import groupBy from 'lodash.groupby';
import partition from 'lodash.partition';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { DataFieldWithDataType } from '../../../../common-types';
import {
  $HierarchicalFilterSeparator$,
  SEGMENT_OTHERS_INDICATOR,
} from '../../../common/api/api-interfaces';
import { CompanySettingsStore } from '../../../common/company-settings-store';
import ServiceExecutorWithRoles from '../../../common/components/query/ServiceExecutorWithRoles';
import {
  DataTypes,
  EmployeeDataFields,
  FinancialDataFields,
} from '../../../common/constants/constants';
import {
  MetricGroupId,
  MetricId,
} from '../../../common/graphql/generated/graphql-sdk';
import { MetricStore } from '../../../common/metrics/metric-store';
import { getMetricSegmentationService } from '../../../common/metrics/services/getMetricServices';
import { Service } from '../../../common/services/utils';
import { sortChartLabelsNew } from '../../../common/utilFunctions/sorters';
import { isHierarchical } from '../../../common/utilFunctions/utils';
import {
  Filter,
  Segment,
} from '../../../common/v2/common/components/filter/filterbar/types';
import { useLegacyDomainScopedStoresContext } from '../../../common/v2/context/contexts';
import {
  GlobalFilterHandle,
  GlobalSegmentHandle,
} from '../../../common/v2/context/types';
import { DataFields } from '../../../common/v2/types';
import { EvaluationStore } from '../evaluation/evaluation-store';
import { BalanceSheetStore } from './balance-sheet-store';
import { PbsSegmentationRow } from './BalanceSheetSegmentationRow';
import {
  MetaDataObject,
  PbsCellDrillDownContext,
  PbsSegmentationService,
  PbsSegmentationServiceInputs,
  PbsSegmentationServiceResponse,
  PbsServiceResponseData,
  PbsTimeHeaderData,
} from './types';
import { allowedFiltersForFinancialMetrics } from './utils';

interface PbsSegmentationRowsProps {
  metric: MetricGroupId;
  selectedDimension: MetricId;
  balanceSheetStore: BalanceSheetStore;
  openSqlModal: (metaDataObject: MetaDataObject) => void;
  filterHandle: GlobalFilterHandle;
  segmentationLevel1Handle: GlobalSegmentHandle;
  segmentationLevel2Handle: GlobalSegmentHandle;
  onCellDrillDown?: (
    context: PbsCellDrillDownContext,
    timeHeadersData?: PbsTimeHeaderData[]
  ) => void;
}

export type MetricToSegmentationServiceMap = Record<
  MetricGroupId,
  PbsSegmentationService
>;

export const getSortedSegmentRows = (
  segmentRows: Record<string, PbsServiceResponseData>[],
  segmentDimension: DataFieldWithDataType,
  companySettingsStore: CompanySettingsStore,
  evaluationStore: EvaluationStore
): Record<string, PbsServiceResponseData>[] => {
  // Note: For hierarchical segments, We are only sorting the labels of the lowest level
  // from the segments selected. We concat the remaining labels at the end of the sorted list.
  // Sorting a mixed bag of segments from different levels with different sort orders
  // would be quite complex so I'm not doing that for now.
  const [othersSegmentRow, segmentRowsWithoutOthers] = partition(
    segmentRows,
    (r) => (Object.keys(r).first() as string).includes(SEGMENT_OTHERS_INDICATOR)
  );
  // I am removing others row from sorting as this will always be at the end
  const segmentLabels = segmentRowsWithoutOthers.map(
    (r) => Object.keys(r).first() as string
  );
  const segmentedRowsObj = Object.fromEntries(
    segmentRowsWithoutOthers.map((r) => [Object.keys(r).first(), r])
  );
  let sortedLabels: string[];
  if (isHierarchical(segmentDimension)) {
    const splitLabelsByLevel = groupBy(
      segmentLabels,
      (l) => l.split($HierarchicalFilterSeparator$).length
    );
    const sortedLabelsOfAllLevels = Object.entries(splitLabelsByLevel)
      .sort(([key1], [key2]) => Number(key1) - Number(key2))
      .flatMap(([key, sameLevelLabels]) =>
        sortChartLabelsNew({
          labels: sameLevelLabels,
          baseDim: segmentDimension,
          companySettingsStore,
          evaluationStore,
        })
      );
    sortedLabels = sortedLabelsOfAllLevels;
  } else {
    sortedLabels = sortChartLabelsNew({
      labels: segmentLabels,
      baseDim: segmentDimension,
      companySettingsStore,
      evaluationStore,
    });
  }
  const sortedSegmentRows = sortedLabels.map(
    (label) => segmentedRowsObj[label]
  );
  return [...sortedSegmentRows, ...othersSegmentRow];
};

export const getModifiedFiltersAndSegments = (
  metric: MetricGroupId,
  {
    filters,
    segmentationLevel1,
    segmentationLevel2,
    metricStore,
  }: {
    filters: Filter[];
    segmentationLevel1: Segment[];
    segmentationLevel2: Segment[];
    metricStore: MetricStore;
  }
) => {
  //TODO: this is temporary until we migrate financialmetrics to cohort table
  const isFinancialMetric = metricStore.isMetricGroupFinancial(metric);
  if (isFinancialMetric) {
    const segmentationLevel1DataField = segmentationLevel1[0]?.property;
    const segmentationLevel2DataField = segmentationLevel2[0]?.property;
    const finalFilters = allowedFiltersForFinancialMetrics(filters);
    const finalSegmentationLevel1 = allowedFiltersForFinancialMetrics(
      segmentationLevel1
    ).flatMap((s) => {
      return segmentationLevel1DataField === EmployeeDataFields.ORGANIZATION ||
        segmentationLevel1DataField === EmployeeDataFields.FUNCTION
        ? [
            {
              ...s,
              dataType: DataTypes.FINANCIALMETRICS,
              property:
                segmentationLevel1DataField === EmployeeDataFields.ORGANIZATION
                  ? FinancialDataFields.ORGANIZATION_LEVEL_1
                  : FinancialDataFields.FUNCTION_LEVEL_1,
            },
          ]
        : [];
    });
    const finalSegmentationLevel2 = allowedFiltersForFinancialMetrics(
      segmentationLevel2
    ).flatMap((s) => {
      return segmentationLevel2DataField === EmployeeDataFields.ORGANIZATION ||
        segmentationLevel2DataField === EmployeeDataFields.FUNCTION
        ? [
            {
              ...s,
              dataType: DataTypes.FINANCIALMETRICS,
              property:
                segmentationLevel2DataField === EmployeeDataFields.ORGANIZATION
                  ? FinancialDataFields.ORGANIZATION_LEVEL_1
                  : FinancialDataFields.FUNCTION_LEVEL_1,
            },
          ]
        : [];
    });
    return { finalFilters, finalSegmentationLevel1, finalSegmentationLevel2 };
  } else if (
    metricStore.isMetricGroupCohort(metric) ||
    metricStore.isMetricGroupEmployeeCohort(metric)
  ) {
    const finalFilters = filters;
    const finalSegmentationLevel1: Segment[] = segmentationLevel1;
    const finalSegmentationLevel2: Segment[] = [];
    return { finalFilters, finalSegmentationLevel1, finalSegmentationLevel2 };
  } else {
    const finalFilters = filters;
    const finalSegmentationLevel1 = segmentationLevel1;
    const finalSegmentationLevel2 = segmentationLevel2;
    return { finalFilters, finalSegmentationLevel1, finalSegmentationLevel2 };
  }
};

export const PbsSegmentationRows = observer(
  (props: PbsSegmentationRowsProps) => {
    const {
      metric,
      selectedDimension,
      balanceSheetStore,
      openSqlModal,
      filterHandle,
      segmentationLevel1Handle,
      segmentationLevel2Handle,
      onCellDrillDown,
    } = props;
    const legacyDomainScopedStores = useLegacyDomainScopedStoresContext();
    const {
      metricStore,
      cachedMasterDataApiService,
      companySettingsStore,
      evaluationStore,
      timeSliderStore,
      benchmarkStore,
    } = legacyDomainScopedStores;
    const service = getMetricSegmentationService(
      metric,
      metricStore,
      cachedMasterDataApiService
    );
    const [filtersState] = filterHandle;
    const { items: filters } = filtersState;
    const [segmentationLevel1State] = segmentationLevel1Handle;
    const { items: segmentationLevel1 } = segmentationLevel1State;
    const [segmentationLevel2State] = segmentationLevel2Handle;
    const { items: segmentationLevel2 } = segmentationLevel2State;
    const { timeSliderConfig } = timeSliderStore;
    const { selectedBenchmark } = benchmarkStore;

    const { finalFilters, finalSegmentationLevel1, finalSegmentationLevel2 } =
      getModifiedFiltersAndSegments(metric, {
        filters,
        segmentationLevel1,
        segmentationLevel2,
        metricStore,
      });

    if (finalSegmentationLevel1.length === 0) {
      return null;
    }

    const inputs: PbsSegmentationServiceInputs = {
      segmentationLevel1: finalSegmentationLevel1,
      segmentationLevel2: finalSegmentationLevel2,
      timeSliderConfig,
      selectedBenchmark,
    };
    return (
      <ServiceExecutorWithRoles
        service={
          service as unknown as Service<
            PbsSegmentationServiceInputs,
            PbsSegmentationServiceResponse
          >
        }
        inputs={toJS(inputs, { recurseEverything: true })}
        filters={toJS(finalFilters)}
      >
        {({ data }) => {
          if (!data || segmentationLevel1.length === 0) return null;
          const segmentationLevel1DataField = {
            dataType: segmentationLevel1[0]?.dataType as DataTypes,
            dataField: segmentationLevel1[0]?.property as DataFields,
          };
          const {
            responseData,
            fixedHeaderObjs,
            timeHeaderObjs,
            benchmarkHeaderObjs,
            totalData,
            meta,
          } = data;
          const segmentRows = Object.values(responseData)[0];
          const sortedSegmentRows = getSortedSegmentRows(
            segmentRows,
            segmentationLevel1DataField,
            companySettingsStore,
            evaluationStore
          );
          return sortedSegmentRows.map((segmentRow, i) => {
            if (!segmentRow) {
              return null;
            }
            const segmentName = Object.keys(segmentRow)[0];
            return (
              <PbsSegmentationRow
                key={segmentName}
                pbsItem={metric}
                timeHeaderObjs={timeHeaderObjs}
                segmentName={segmentName}
                data={Object.values(segmentRow)[0]}
                totalData={totalData}
                metaDataForRow={meta?.[metric]?.[segmentName]}
                fixedHeaderObjs={fixedHeaderObjs}
                benchmarkHeaderObjs={benchmarkHeaderObjs}
                selectedMeasure={selectedDimension}
                isFirstRow={i === 0}
                isLastRow={i === segmentRows.length - 1}
                isOnlyRow={segmentRows.length === 1}
                balanceSheetStore={balanceSheetStore}
                openSqlModal={openSqlModal}
                segmentLevel={1}
                segmentationLevel1Handle={segmentationLevel1Handle}
                segmentationLevel2Handle={segmentationLevel2Handle}
                onCellDrillDown={onCellDrillDown}
              />
            );
          });
        }}
      </ServiceExecutorWithRoles>
    );
  }
);
